const GALLERY_SELECTOR = '[data-card-gallery-swap]';
const registered = new WeakSet();
const states = new WeakMap();

let pinching = false;
let nearViewportObserver;

function releaseImages(root) {
  root.querySelectorAll?.('img').forEach((image) => {
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.removeAttribute('src');
  });
}

function updateDots(gallery, activeIndex) {
  gallery.querySelectorAll('[data-card-gallery-swap-dots] span').forEach((dot, index) => {
    dot.classList.toggle('is-active', index === activeIndex);
  });
}

function loadSlideImages(source, state, index) {
  const images = Array.from(source.querySelectorAll('img'));
  if (images.length === 0) return Promise.resolve(true);

  const warmImages = [];
  const loading = Promise.all(images.map((sourceImage) => new Promise((resolve) => {
    const image = new Image();
    warmImages.push(image);
    const src = sourceImage.getAttribute('src');
    const srcset = sourceImage.getAttribute('srcset');
    const sizes = sourceImage.getAttribute('sizes');

    if (!src && !srcset) {
      resolve(true);
      return;
    }

    image.onload = async () => {
      try {
        await image.decode();
      } catch (_) {
        // A completed load is still safe to display when decode() is unavailable.
      }
      resolve(true);
    };
    image.onerror = () => resolve(false);

    if (sizes) image.sizes = sizes;
    if (srcset) image.srcset = srcset;
    if (src) image.src = src;
  }))).then((results) => results.every(Boolean));

  return loading.then((loaded) => {
    if (loaded && state.visible) state.warmImages.set(index, warmImages);
    return loaded;
  });
}

function prepareSlide(gallery, index) {
  const state = states.get(gallery);
  const template = gallery.querySelector('template[data-card-gallery-swap-template]');
  if (!state || !(template instanceof HTMLTemplateElement)) return Promise.resolve(false);

  if (state.preloads.has(index)) return state.preloads.get(index);

  const source = template.content.querySelector(`[data-card-gallery-swap-slide][data-slide-index="${index}"]`);
  if (!(source instanceof HTMLElement)) return Promise.resolve(false);

  const preload = loadSlideImages(source, state, index);
  state.preloads.set(index, preload);
  return preload;
}

async function showSlide(gallery, index) {
  const state = states.get(gallery);
  const stage = gallery.querySelector('[data-card-gallery-swap-stage]');
  const template = gallery.querySelector('template[data-card-gallery-swap-template]');
  if (!state || !(stage instanceof HTMLElement) || !(template instanceof HTMLTemplateElement)) return;

  const source = template.content.querySelector(`[data-card-gallery-swap-slide][data-slide-index="${index}"]`);
  if (!(source instanceof HTMLElement)) return;
  if (state.busy) return;

  state.busy = true;
  const requestId = ++state.requestId;
  const loaded = await prepareSlide(gallery, index);
  if (!loaded || requestId !== state.requestId || !gallery.isConnected) {
    state.busy = false;
    return;
  }

  const nextSlide = source.cloneNode(true);
  nextSlide.querySelectorAll('img').forEach((image) => {
    image.loading = 'eager';
  });

  // Decode the exact image elements that will enter the stage before starting
  // the animation. Safari can otherwise decode a cloned image mid-transition,
  // even when an identical off-DOM Image was already warmed.
  await Promise.all(Array.from(nextSlide.querySelectorAll('img')).map(async (image) => {
    try {
      await image.decode();
    } catch (_) {
      // The preload already completed; continue when decode() is unavailable.
    }
  }));

  if (requestId !== state.requestId || !gallery.isConnected) {
    state.busy = false;
    return;
  }
  const previousSlides = Array.from(stage.children);
  const previousSlide = previousSlides[0];
  const direction = index > state.index ? 1 : -1;
  state.index = index;
  gallery.dataset.currentIndex = String(index);
  updateDots(gallery, index);

  const slideCount = Number(gallery.dataset.slideCount || 1);
  if (state.visible && index < slideCount - 1) prepareSlide(gallery, index + 1);

  if (pinching || !(previousSlide instanceof HTMLElement) || typeof nextSlide.animate !== 'function') {
    stage.replaceChildren(nextSlide);
    previousSlides.forEach(releaseImages);
    state.busy = false;
    return;
  }

  nextSlide.style.position = 'absolute';
  nextSlide.style.inset = '0';
  nextSlide.style.pointerEvents = 'none';
  stage.append(nextSlide);

  const incomingStart = direction > 0 ? '-100%' : '100%';
  const outgoingEnd = direction > 0 ? '100%' : '-100%';
  const timing = { duration: 220, easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)', fill: 'forwards' };
  const outgoingAnimation = previousSlide.animate(
    [{ transform: 'translateX(0)' }, { transform: `translateX(${outgoingEnd})` }],
    timing
  );
  const incomingAnimation = nextSlide.animate(
    [{ transform: `translateX(${incomingStart})` }, { transform: 'translateX(0)' }],
    timing
  );

  await Promise.allSettled([outgoingAnimation.finished, incomingAnimation.finished]);
  outgoingAnimation.cancel();
  incomingAnimation.cancel();

  if (!nextSlide.isConnected) {
    state.busy = false;
    return;
  }
  nextSlide.style.removeProperty('position');
  nextSlide.style.removeProperty('inset');
  nextSlide.style.removeProperty('pointer-events');
  stage.replaceChildren(nextSlide);
  previousSlides.forEach(releaseImages);
  state.busy = false;
}

function register(gallery) {
  if (registered.has(gallery)) return;
  registered.add(gallery);

  const stage = gallery.querySelector('[data-card-gallery-swap-stage]');
  const slideCount = Number(gallery.dataset.slideCount || 1);
  if (!(stage instanceof HTMLElement) || slideCount < 2) return;

  const state = {
    index: Math.min(Number(gallery.dataset.currentIndex || 0), slideCount - 1),
    startX: 0,
    startY: 0,
    tracking: false,
    horizontalLock: false,
    suppressClickUntil: 0,
    requestId: 0,
    busy: false,
    preloads: new Map(),
    warmImages: new Map(),
    visible: false,
  };
  states.set(gallery, state);
  nearViewportObserver?.observe(gallery);

  stage.addEventListener('touchstart', (event) => {
    if (pinching || event.touches.length !== 1) {
      state.tracking = false;
      return;
    }
    state.startX = event.touches[0].clientX;
    state.startY = event.touches[0].clientY;
    state.tracking = true;
    state.horizontalLock = false;

    // Usually the user advances through the gallery, so warm the next image
    // as soon as a single-finger interaction begins.
    if (state.index < slideCount - 1) prepareSlide(gallery, state.index + 1);
  }, { passive: true });

  stage.addEventListener('touchmove', (event) => {
    if (!state.tracking || event.touches.length !== 1) {
      state.tracking = false;
      state.horizontalLock = false;
      return;
    }

    const deltaX = event.touches[0].clientX - state.startX;
    const deltaY = event.touches[0].clientY - state.startY;
    if (!state.horizontalLock && Math.abs(deltaX) >= 8 && Math.abs(deltaX) > Math.abs(deltaY) * 1.15) {
      state.horizontalLock = true;
    }
    if (state.horizontalLock) event.preventDefault();
  }, { passive: false });

  stage.addEventListener('touchend', (event) => {
    if (!state.tracking || pinching || event.changedTouches.length === 0) {
      state.tracking = false;
      return;
    }

    const deltaX = event.changedTouches[0].clientX - state.startX;
    const deltaY = event.changedTouches[0].clientY - state.startY;
    state.tracking = false;
    state.horizontalLock = false;
    if (Math.abs(deltaX) < 30 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;

    state.suppressClickUntil = performance.now() + 450;
    // RTL storefront: dragging right advances; dragging left goes back.
    const nextIndex = deltaX > 0
      ? Math.min(state.index + 1, slideCount - 1)
      : Math.max(state.index - 1, 0);
    if (nextIndex !== state.index) showSlide(gallery, nextIndex);
  }, { passive: true });

  stage.addEventListener('touchcancel', () => {
    state.tracking = false;
    state.horizontalLock = false;
  }, { passive: true });

  gallery.addEventListener('click', (event) => {
    if (performance.now() >= state.suppressClickUntil) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, { capture: true });
}

function registerAll(root = document) {
  if (root instanceof HTMLElement && root.matches(GALLERY_SELECTOR)) register(root);
  root.querySelectorAll?.(GALLERY_SELECTOR).forEach((gallery) => {
    if (gallery instanceof HTMLElement) register(gallery);
  });
}

nearViewportObserver = new IntersectionObserver((entries) => {
  entries.forEach((entry) => {
    if (!(entry.target instanceof HTMLElement)) return;

    const gallery = entry.target;
    const state = states.get(gallery);
    if (!state) return;

    state.visible = entry.isIntersecting;

    if (!entry.isIntersecting) {
      // Drop strong references to decoded preload images when the card leaves
      // the viewport. The browser may then reclaim their decoded memory.
      state.warmImages.clear();
      state.preloads.clear();
      return;
    }

    const slideCount = Number(gallery.dataset.slideCount || 1);
    if (state.index < slideCount - 1) prepareSlide(gallery, state.index + 1);
  });
}, {
  rootMargin: '0px',
  threshold: 0.01,
});

registerAll();

const resultsList = document.querySelector('results-list');
if (resultsList) {
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node instanceof HTMLElement) registerAll(node);
      });
    });
  }).observe(resultsList, { childList: true, subtree: true });
}

document.addEventListener('touchstart', (event) => {
  if (event.touches.length >= 2) pinching = true;
}, { passive: true, capture: true });

document.addEventListener('touchend', (event) => {
  if (event.touches.length === 0) pinching = false;
}, { passive: true, capture: true });

document.addEventListener('touchcancel', () => {
  pinching = false;
}, { passive: true, capture: true });

document.addEventListener('gesturestart', () => {
  pinching = true;
}, { passive: true, capture: true });

document.addEventListener('gestureend', () => {
  pinching = false;
}, { passive: true, capture: true });
